import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { FC } from 'react';
import { compactCardContentSx, formatShare, SignupStages, stageShare } from '../../helpers';
import { SignupConversion } from '../../types';

type SignupConversionSummaryProps = {
  conversion: SignupConversion | null;
  isLoading: boolean;
};

/** Expired is laid out apart from the row of stages: it is where a funnel ended, not a stage it passed. */
export const SignupConversionSummary: FC<SignupConversionSummaryProps> = ({ conversion, isLoading }) => {
  if (conversion == null) {
    return isLoading ? <LinearProgress /> : null;
  }

  const { total, sources } = conversion;

  return (
    <Stack gap={1}>
      {SignupStages.map(stage => (
        <Card key={stage.key}>
          <CardContent sx={compactCardContentSx} data-testid={`stage-${stage.key}`}>
            <Typography color="textSecondary" variant="subtitle2">
              {stage.label}
            </Typography>
            <Stack direction="row" alignItems="baseline" gap={1}>
              <Typography variant="h5">{total[stage.key].toLocaleString()}</Typography>
              <Typography variant="body2" color="textSecondary">
                {formatShare(stageShare(total, stage.key))}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ))}

      <Card variant="outlined">
        <CardContent sx={compactCardContentSx} data-testid="expired">
          <Typography color="textSecondary" variant="subtitle2">
            Expired
          </Typography>
          <Typography variant="h6">{total.expired.toLocaleString()}</Typography>
          <Typography variant="caption" color="textSecondary">
            Already counted in the stages they passed
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              {SignupStages.map(stage => (
                <TableCell key={stage.key} align="right">
                  {stage.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sources.map(row => (
              <TableRow key={row.source}>
                <TableCell>{row.source}</TableCell>
                {SignupStages.map(stage => (
                  <TableCell key={stage.key} align="right">
                    {row[stage.key].toLocaleString()}
                    <Typography variant="caption" color="textSecondary" ml={0.5}>
                      {formatShare(stageShare(row, stage.key))}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};
